import { useToast } from 'src/app/useToast';
import { useAppStore } from 'src/context/AppContext';
import { useStowageStore } from 'src/context/StowageContext';
import { copyUpdatedStowagePlanMetaData, mapStowagePlan } from 'src/factory/Stowage/StowagePlanFactory';
import { StowagePlanBM } from 'src/types/BusinessModel/Stowage/StowagePlanBM';
import { StowagePlanTypeEnum } from 'src/types/ViewModel/Stowage/Enums/StowagePlanTypeEnum';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { getMasterStowagePlan, sortStowagePlans } from 'src/utils/stowage/stowagePlanUtils';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';

export const useStowagePlanResponseHandler = () => {
    const { stowagePlans, setStowagePlans, activeStowagePlan, setIsCopyStowagePlanMenuOpen, setActiveStowagePlan, setLoadedMasterStowagePlan } =
        useStowageStore();
    const { toastSuccess } = useToast();
    const { setAppModalData } = useAppStore();
    const { setQueryParamStowagePlan, clearQueryParamsStowagePlan } = useStowageQueryParamsUtils();

    const onStowagePlanUpdated = (stowagePlanBM: StowagePlanBM, sender: string) => {
        const updatedPlan = mapStowagePlan(stowagePlanBM);
        const originalIndex = stowagePlans.findIndex((sp) => sp.stowagePlanGuid === updatedPlan.stowagePlanGuid);
        const originalPlan = originalIndex >= 0 ? stowagePlans[originalIndex] : undefined;

        // Handle deletion
        if (updatedPlan.isDeleted && originalPlan && updatedPlan.type === StowagePlanTypeEnum.Draft) {
            const filteredPlans = stowagePlans.filter((sp) => sp.stowagePlanGuid !== updatedPlan.stowagePlanGuid);
            const deleteMsg = `${sender} has deleted ${getStowagePlanName(updatedPlan)}`;
            const isActivePlan = updatedPlan.stowagePlanGuid === activeStowagePlan?.stowagePlanGuid;

            if (isActivePlan) {
                setAppModalData({
                    title: deleteMsg,
                    message: `${deleteMsg}. The stowage plan you are currently working on has been deleted. Please make a copy in order to save your plan, or remove it to discard changes.`,
                    actionButtonText: 'Copy stowage plan',
                    cancelButtonText: 'OK',
                    action: () => setIsCopyStowagePlanMenuOpen(true),
                    cancel: () => {
                        setStowagePlans(filteredPlans);
                        const nextPlanGuid = getMasterStowagePlan(filteredPlans)?.stowagePlanGuid || filteredPlans[0]?.stowagePlanGuid;
                        nextPlanGuid ? setQueryParamStowagePlan(nextPlanGuid) : clearQueryParamsStowagePlan();
                        setAppModalData(null);
                    },
                });
            } else {
                setStowagePlans(filteredPlans);
                toastSuccess(deleteMsg);
            }
            return;
        }

        // Handle update or create
        const newPlans = originalPlan
            ? stowagePlans.map((sp) => (sp.stowagePlanGuid === updatedPlan.stowagePlanGuid ? copyUpdatedStowagePlanMetaData(sp, updatedPlan) : sp))
            : sortStowagePlans([...stowagePlans, updatedPlan]);

        if (updatedPlan.stowagePlanGuid === activeStowagePlan?.stowagePlanGuid) {
            setActiveStowagePlan(copyUpdatedStowagePlanMetaData(activeStowagePlan, updatedPlan));
        } else if (updatedPlan.type === StowagePlanTypeEnum.Master && activeStowagePlan.type === StowagePlanTypeEnum.Master) {
            setQueryParamStowagePlan(updatedPlan.stowagePlanGuid);
        }

        setStowagePlans([...newPlans]);
        toastSuccess(getStowagePlanChangeText(sender, updatedPlan, originalPlan));
    };

    const onStowagePlanAdded = (stowagePlanBM: StowagePlanBM, sender: string) => {
        const newPlan = mapStowagePlan(stowagePlanBM);

        setStowagePlans(sortStowagePlans([...stowagePlans, newPlan]));
        toastSuccess(`${sender} has added a new stowage plan ${getStowagePlanName(newPlan)}`);
    };

    const updateLoadedMasterStowagePlan = (stowagePlanBM: StowagePlanBM, sender: string) => {
        const updatedPlan = mapStowagePlan(stowagePlanBM);

        setLoadedMasterStowagePlan(updatedPlan);
        toastSuccess(getStowagePlanChangeText(sender, updatedPlan));
    };

    const getStowagePlanChangeText = (sender: string, updatedPlan: StowagePlan, originalPlan?: StowagePlan) => {
        if (!originalPlan || originalPlan.type !== updatedPlan.type) {
            if (updatedPlan.type === StowagePlanTypeEnum.Master) {
                return `${sender} has promoted ${getStowagePlanName(updatedPlan)} to master plan`;
            }
            if (updatedPlan.type === StowagePlanTypeEnum.Published) {
                return `${sender} has published ${getStowagePlanName(updatedPlan)}`;
            }
        }

        if (originalPlan && updatedPlan.title !== originalPlan.title) {
            return `${sender} has renamed ${getStowagePlanName(originalPlan)} to ${getStowagePlanName(updatedPlan)}`;
        }

        return `${sender} has updated ${getStowagePlanName(updatedPlan)}`;
    };

    const getStowagePlanName = (stowagePlan: StowagePlan) => `M${stowagePlan.masterPlanVersion} ${stowagePlan.title}`;

    return { onStowagePlanUpdated, onStowagePlanAdded, updateLoadedMasterStowagePlan };
};
